/**
 * UserProfileCard.tsx
 *
 * Renders the `user_profile` envelope data emitted by onething_get_user.
 * Shows profile header, contact/org lines, and active priorities with
 * recent report snippets.
 */

import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getPriorityStatusMeta } from "@/components/priorities/priorityCardTokens";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PriorityStatus } from "@/types";
import { getAvatarGradient, getInitials } from "./avatarColor";

// ---------------------------------------------------------------------------
// Data shapes emitted by the BE envelope for kind "user_profile"
// ---------------------------------------------------------------------------

export interface RecentReport {
  reportDate: string;
  blocker: boolean;
  blockedReason?: string;
  content?: string;
}

export interface ActivePriority {
  code: string;
  name: string;
  status: PriorityStatus;
  recentReports?: RecentReport[];
}

export interface UserProfileData {
  pid: string;
  fullName: string;
  email?: string;
  position?: string;
  managerName?: string;
  teamNames?: string[];
  activePriorities?: ActivePriority[];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ReportLine({ report }: { report: RecentReport }) {
  return (
    <div className="mt-1 ml-0.5 pl-2 border-l-2 border-border/60">
      {report.blocker ? (
        <div className="flex items-start gap-1 text-[10.5px] text-red-600 dark:text-red-400">
          <AlertTriangle className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-mono text-muted-foreground/70 mr-1">{report.reportDate}</span>
            {report.content ?? report.blockedReason}
          </span>
        </div>
      ) : (
        <p className="text-[10.5px] text-muted-foreground/80">
          <span className="font-mono text-muted-foreground/60 mr-1">{report.reportDate}</span>
          {report.content}
        </p>
      )}
    </div>
  );
}

function PriorityLine({ priority }: { priority: ActivePriority }) {
  const statusMeta = getPriorityStatusMeta(priority.status);
  const StatusIcon = statusMeta.icon;
  const reports = priority.recentReports ?? [];

  return (
    <div className="mx-1 rounded-lg px-2 py-2 transition-colors hover:bg-muted/50 cursor-default">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">{priority.code}</span>
        <span className="text-[12.5px] font-semibold tracking-tight leading-tight flex-1 min-w-0">
          {priority.name}
        </span>
        <PriorityStatusTooltip status={priority.status} className="flex-shrink-0">
          <span
            className={`inline-flex items-center gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 border flex-shrink-0 ${statusMeta.className}`}
          >
            <StatusIcon className="h-2.5 w-2.5" />
            {statusMeta.label}
          </span>
        </PriorityStatusTooltip>
      </div>
      {reports.slice(0, 2).map((r, i) => (
        <ReportLine key={i} report={r} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface UserProfileCardProps {
  data: UserProfileData;
}

export function UserProfileCard({ data }: UserProfileCardProps) {
  const priorities = data.activePriorities ?? [];

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      {/* Profile header */}
      <CardHeader className="flex flex-row items-center gap-3 px-4 py-3.5">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white ${getAvatarGradient(data.pid)}`}
        >
          {getInitials(data.fullName)}
        </div>
        <div className="min-w-0">
          <p className="text-[13.5px] font-bold leading-tight tracking-tight">{data.fullName}</p>
          {data.position && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{data.position}</p>
          )}
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-3.5 pt-0 space-y-3">
        {/* Contact / org info */}
        <div className="space-y-0.5">
          {data.email && <p className="text-[11px] text-muted-foreground">{data.email}</p>}
          {data.managerName && (
            <p className="text-[11px] text-muted-foreground">
              <span className="text-muted-foreground/60">Reports to: </span>
              {data.managerName}
            </p>
          )}
          {data.teamNames && data.teamNames.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              <span className="text-muted-foreground/60">Teams: </span>
              {data.teamNames.join(", ")}
            </p>
          )}
        </div>

        {/* Active priorities */}
        {priorities.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                Active Priorities
              </span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                {priorities.length}
              </span>
            </div>
            <div className="-mx-1">
              {priorities.map((p) => (
                <PriorityLine key={p.code} priority={p} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
